import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./HeatAnnexure.css";

function HeatAnnexure() {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // View mode: 'list' or 'form'
  const [viewMode, setViewMode] = useState("list");
  const [editingId, setEditingId] = useState(null);

  // Filter states
  const [filterComponent, setFilterComponent] = useState("");
  const [filterHeatNumber, setFilterHeatNumber] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    components: [],
    heatNumbers: []
  });

  // Dropdown options for the form
  const [dropdownOptions, setDropdownOptions] = useState({
    components: [],
    heatNumbers: []
  });

  // Attachment states
  const [attachmentFile, setAttachmentFile] = useState(null);       // File object for new upload
  const [attachmentPreview, setAttachmentPreview] = useState(null);  // Preview URL (data URL or server URL)
  const [existingAttachment, setExistingAttachment] = useState(null); // { filename, path } from server
  const [removeAttachment, setRemoveAttachment] = useState(false);   // Flag to remove attachment on save
  const [fileError, setFileError] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);        // Show compression loading
  const fileInputRef = useRef(null);
  const [materialAutoFilled, setMaterialAutoFilled] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    component: "",
    heat_number: "",
    material: "",
    tc_no: "",
    vendor_name: "",
    chemical_analysis: {
      c: "",
      si: "",
      mn: "",
      p: "",
      s: "",
      cr: "",
      mo: "",
      ni: ""
    },
    mechanical_analysis: {
      specified_requirement: {
        uts_mpa: "",
        ys_1_mpa: "",
        ys_2_mpa: "",
        el_percent: "",
        ra_percent: "",
        hardness_bhn: ""
      },
      impact_test_1: {
        temperature: "0.00",
        min_joule: "",
        v1: "",
        v2: "",
        v3: "",
        avg: ""
      },
      impact_test_2: {
        temperature: "0.00",
        min_joule: "",
        v1: "",
        v2: "",
        v3: "",
        avg: ""
      }
    }
  });

  // Fetch configurations
  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterComponent) params.component = filterComponent;
      if (filterHeatNumber) params.heat_number = filterHeatNumber;

      const response = await axios.get("/heat-annexure", { params });
      setConfigurations(response.data.data || []);
    } catch (err) {
      setError("Failed to fetch heat annexure configurations");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown options for form
  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get("/heat-annexure/options");
      setDropdownOptions(response.data.data || { components: [], heatNumbers: [] });
    } catch (err) {
      console.error("Failed to fetch dropdown options:", err);
    }
  };

  // Fetch filter options from existing configurations
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get("/heat-annexure/filter-options");
      setFilterOptions(response.data.data || { components: [], heatNumbers: [] });
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };

  useEffect(() => {
    fetchConfigurations();
    fetchDropdownOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [filterComponent, filterHeatNumber]);

  // Fetch material value when component, heat_number, and tc_no are all set
  useEffect(() => {
    const fetchMaterial = async () => {
      // Only fetch if ALL three fields are filled
      if (formData.component && formData.heat_number && formData.tc_no) {
        try {
          const response = await axios.get("/heat-annexure/material/search", {
            params: {
              component: formData.component,
              heat_number: formData.heat_number,
              tc_no: formData.tc_no
            }
          });
          if (response.data.data?.material) {
            setFormData(prev => ({
              ...prev,
              material: response.data.data.material
            }));
            setMaterialAutoFilled(true);
          } else {
            // Clear material if no match found
            if (materialAutoFilled) {
              setFormData(prev => ({
                ...prev,
                material: ""
              }));
              setMaterialAutoFilled(false);
            }
          }
        } catch (err) {
          console.error("Failed to fetch material:", err);
          if (materialAutoFilled) {
            setFormData(prev => ({
              ...prev,
              material: ""
            }));
            setMaterialAutoFilled(false);
          }
        }
      } else {
        // Clear material if any required field is empty
        if (materialAutoFilled) {
          setFormData(prev => ({
            ...prev,
            material: ""
          }));
          setMaterialAutoFilled(false);
        }
      }
    };

    fetchMaterial();
  }, [formData.component, formData.heat_number, formData.tc_no, materialAutoFilled]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("chemical_")) {
      const field = name.replace("chemical_", "");
      setFormData(prev => ({
        ...prev,
        chemical_analysis: {
          ...prev.chemical_analysis,
          [field]: value
        }
      }));
    } else if (name.startsWith("mechanical_")) {
      const parts = name.replace("mechanical_", "").split("_");
      const section = parts[0]; // 'sr', 'impact1', 'impact2'
      const field = parts.slice(1).join("_");

      if (section === "sr") {
        setFormData(prev => ({
          ...prev,
          mechanical_analysis: {
            ...prev.mechanical_analysis,
            specified_requirement: {
              ...prev.mechanical_analysis.specified_requirement,
              [field]: value
            }
          }
        }));
      } else if (section === "impact1") {
        setFormData(prev => ({
          ...prev,
          mechanical_analysis: {
            ...prev.mechanical_analysis,
            impact_test_1: {
              ...prev.mechanical_analysis.impact_test_1,
              [field]: value
            }
          }
        }));
      } else if (section === "impact2") {
        setFormData(prev => ({
          ...prev,
          mechanical_analysis: {
            ...prev.mechanical_analysis,
            impact_test_2: {
              ...prev.mechanical_analysis.impact_test_2,
              [field]: value
            }
          }
        }));
      }
    } else {
      if (name === "material") {
        setMaterialAutoFilled(false);
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ── FILE UPLOAD HANDLERS ──────────────────────────────────────

  // Compress image on client side if needed (returns a Promise<Blob>)
  const compressImage = (file, maxSizeKB = 400) => {
    return new Promise((resolve, reject) => {
      const maxSize = maxSizeKB * 1024;
      if (file.size <= maxSize) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let { width, height } = img;

          // Scale down progressively if still too large
          const maxDim = 1200;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Try decreasing quality until under maxSize
          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= maxSize || quality <= 0.1) {
                  const compressedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                } else {
                  quality -= 0.15;
                  tryCompress();
                }
              },
              file.type,
              quality
            );
          };
          tryCompress();
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  // Compress PDF on client side using pdf-lib
  const compressPdf = async (file, maxSizeKB = 400) => {
    const maxSize = maxSizeKB * 1024;
    if (file.size <= maxSize) {
      return file;
    }

    // Dynamically import pdf-lib
    const { PDFDocument } = await import("pdf-lib");
    
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    
    // Try to compress by removing unnecessary metadata
    // Note: pdf-lib doesn't support compression well, so we try various approaches
    const pages = pdfDoc.getPages();
    
    // Remove duplicate fonts and resources if possible
    pdfDoc.setTitle("");
    pdfDoc.setAuthor("");
    pdfDoc.setSubject("");
    pdfDoc.setKeywords([]);
    pdfDoc.setProducer("");
    pdfDoc.setCreator("");
    
    // Save with maximum compression
    let compressedPdf = await pdfDoc.save({ useObjectStreams: false });
    
    // If still too large, try scaling down pages (for PDFs with large images)
    let attempts = 0;
    while (compressedPdf.byteLength > maxSize && attempts < 3) {
      attempts++;
      
      // Try scaling images in the PDF
      const images = await pdfDoc.getImages();
      // Note: pdf-lib can't easily re-compress embedded images
      // As a fallback, return the file as-is if we can't compress enough
      if (attempts >= 3) {
        break;
      }
      
      compressedPdf = await pdfDoc.save({ useObjectStreams: false });
    }
    
    // Check if compression was successful
    if (compressedPdf.byteLength > maxSize) {
      throw new Error("Unable to compress PDF below 400 KB");
    }
    
    return new File([compressedPdf], file.name, {
      type: "application/pdf",
      lastModified: Date.now()
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setFileError(null);
    setIsCompressing(true);

    if (!file) {
      setIsCompressing(false);
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setFileError("Only JPG, PNG, and PDF files are allowed.");
      setIsCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // File size validation: max 1 MB
    if (file.size > 1024 * 1024) {
      setFileError("File size must be less than or equal to 1 MB.");
      setIsCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      let processedFile = file;

      // Conditional compression: only compress if file is between 400 KB and 1 MB
      if (file.size > 400 * 1024 && file.size <= 1024 * 1024) {
        setIsCompressing(true);
        
        if (file.type.startsWith("image/")) {
          // Compress image
          processedFile = await compressImage(file, 400);
        } else if (file.type === "application/pdf") {
          // Try to compress PDF
          try {
            processedFile = await compressPdf(file, 400);
          } catch (pdfErr) {
            setFileError("Unable to compress PDF below 400 KB. Please upload a smaller file.");
            setIsCompressing(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }
        }
      }

      // Validate file size after compression
      if (processedFile.size > 400 * 1024) {
        setFileError("File size must be less than 400 KB. Please upload a smaller file.");
        setIsCompressing(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setAttachmentFile(processedFile);
      setRemoveAttachment(false);

      // Generate preview
      if (processedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => setAttachmentPreview({ type: "image", url: ev.target.result, name: processedFile.name });
        reader.readAsDataURL(processedFile);
      } else {
        // PDF: just show filename
        setAttachmentPreview({ type: "pdf", name: processedFile.name });
      }
    } catch (err) {
      setFileError("Failed to process file: " + err.message);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setRemoveAttachment(true);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Get the API URL for serving attachment from database
  const getAttachmentUrl = (configId) => {
    if (!configId) return null;
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return `${baseUrl}/heat-annexure/${configId}/attachment`;
  };

  // Open attachment in a new tab using attachment_url from API
  const openAttachmentPreview = (e, config) => {
    e.preventDefault();
    if (config.attachment_url) {
      window.open(config.attachment_url, "_blank");
    } else {
      setError("Preview not available");
    }
  };

  // ── END FILE UPLOAD HANDLERS ──────────────────────────────────

  // Reset form
  const resetForm = () => {
    setFormData({
      component: "",
      heat_number: "",
      material: "",
      tc_no: "",
      vendor_name: "",
      chemical_analysis: {
        c: "",
        si: "",
        mn: "",
        p: "",
        s: "",
        cr: "",
        mo: "",
        ni: ""
      },
      mechanical_analysis: {
        specified_requirement: {
          uts_mpa: "",
          ys_1_mpa: "",
          ys_2_mpa: "",
          el_percent: "",
          ra_percent: "",
          hardness_bhn: ""
        },
        impact_test_1: {
          temperature: "0.00",
          min_joule: "",
          v1: "",
          v2: "",
          v3: "",
          avg: ""
        },
        impact_test_2: {
          temperature: "0.00",
          min_joule: "",
          v1: "",
          v2: "",
          v3: "",
          avg: ""
        }
      }
    });
    setEditingId(null);
    setMaterialAutoFilled(false);
    // Reset attachment states
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setExistingAttachment(null);
    setRemoveAttachment(false);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Open form for new configuration
  const handleAddNew = () => {
    resetForm();
    setViewMode("form");
  };

  // Open form for editing
  const handleEdit = async (config) => {
    setFormData({
      component: config.component || "",
      heat_number: config.heat_number || "",
      material: config.material || "",
      tc_no: config.tc_no || "",
      vendor_name: config.vendor_name || "",
      chemical_analysis: config.chemical_analysis || {
        c: "",
        si: "",
        mn: "",
        p: "",
        s: "",
        cr: "",
        mo: "",
        ni: ""
      },
      mechanical_analysis: config.mechanical_analysis || {
        specified_requirement: {
          uts_mpa: "",
          ys_1_mpa: "",
          ys_2_mpa: "",
          el_percent: "",
          ra_percent: "",
          hardness_bhn: ""
        },
        impact_test_1: {
          temperature: "0.00",
          min_joule: "",
          v1: "",
          v2: "",
          v3: "",
          avg: ""
        },
        impact_test_2: {
          temperature: "0.00",
          min_joule: "",
          v1: "",
          v2: "",
          v3: "",
          avg: ""
        }
      }
    });
    setMaterialAutoFilled(false);

    // Set existing attachment if present
    if (config.attachment_filename) {
      const isImage = /\.(jpg|jpeg|png)$/i.test(config.attachment_filename);
      setExistingAttachment({
        filename: config.attachment_filename,
        id: config.id
      });
      if (isImage) {
        setAttachmentPreview({
          type: "image",
          url: config.attachment_url || getAttachmentUrl(config.id),
          name: config.attachment_filename
        });
      } else {
        setAttachmentPreview({
          type: "pdf",
          name: config.attachment_filename,
          url: config.attachment_url || getAttachmentUrl(config.id)
        });
      }
    } else {
      setExistingAttachment(null);
      setAttachmentPreview(null);
    }

    setAttachmentFile(null);
    setRemoveAttachment(false);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    setEditingId(config.id);
    setViewMode("form");
  };

  // Back to list
  const handleBackToList = () => {
    resetForm();
    setViewMode("list");
    setError(null);
    setSuccess(null);
  };

  // Handle form submission
  const handleSubmit = async (e, addAnother = false) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!formData.component || !formData.heat_number) {
      setError("Component and Heat Number are required fields");
      return;
    }

    try {
      setLoading(true);

      // Build FormData for multipart upload
      const submitData = new FormData();
      submitData.append("component", formData.component);
      submitData.append("heat_number", formData.heat_number);
      submitData.append("material", formData.material || "");
      submitData.append("tc_no", formData.tc_no || "");
      submitData.append("vendor_name", formData.vendor_name || "");
      submitData.append("chemical_analysis", JSON.stringify(formData.chemical_analysis));
      submitData.append("mechanical_analysis", JSON.stringify(formData.mechanical_analysis));

      // Handle attachment
      if (attachmentFile) {
        submitData.append("attachment", attachmentFile);
      }
      if (removeAttachment) {
        submitData.append("remove_attachment", "true");
      }

      if (editingId) {
        await axios.put(`/heat-annexure/${editingId}`, submitData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setSuccess("Heat annexure configuration updated successfully");
        if (!addAnother) {
          setTimeout(() => {
            handleBackToList();
            fetchConfigurations();
            fetchFilterOptions();
          }, 1000);
        }
      } else {
        await axios.post("/heat-annexure", submitData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setSuccess("Heat annexure configuration created successfully");

        if (addAnother) {
          resetForm();
          fetchConfigurations();
          fetchFilterOptions();
        } else {
          setTimeout(() => {
            handleBackToList();
            fetchConfigurations();
            fetchFilterOptions();
          }, 1000);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save heat annexure configuration");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this configuration?")) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/heat-annexure/${id}`);
      setSuccess("Heat annexure configuration deleted successfully");
      fetchConfigurations();
      fetchFilterOptions();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete heat annexure configuration");
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilterComponent("");
    setFilterHeatNumber("");
  };

  // Render List View
  const renderListView = () => (
    <>
      {/* Header */}
      <div className="heat-annexure-header">
        <button className="back-button" onClick={() => navigate("/admin")}>
          &larr; Back to Admin Panel
        </button>
        <div className="header-content">
          <h1>Heat Annexure Configuration</h1>
          <p>Manage heat annexure mappings for Component and Heat Number combinations</p>
        </div>
        <button className="add-config-btn" onClick={handleAddNew}>
          + Add New Configuration
        </button>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filter Configurations</h3>
        <div className="filters-row">
          <div className="filter-group">
            <label>Component</label>
            <select value={filterComponent} onChange={(e) => setFilterComponent(e.target.value)}>
              <option value="">All Components</option>
              {filterOptions.components.map((comp) => (
                <option key={comp} value={comp}>{comp}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Heat Number</label>
            <select value={filterHeatNumber} onChange={(e) => setFilterHeatNumber(e.target.value)}>
              <option value="">All Heat Numbers</option>
              {filterOptions.heatNumbers.map((hn) => (
                <option key={hn} value={hn}>{hn}</option>
              ))}
            </select>
          </div>
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Configurations Table */}
      <div className="configs-table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : configurations.length === 0 ? (
          <div className="empty-state">
            <p>No heat annexure configurations found</p>
            <button onClick={handleAddNew}>Create First Configuration</button>
          </div>
        ) : (
          <table className="configs-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Heat Number</th>
                <th>Material</th>
                <th>TC No.</th>
                <th>Vendor Name</th>
                <th>Attachment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configurations.map((config) => (
                <tr key={config.id}>
                  <td>{config.component}</td>
                  <td>{config.heat_number}</td>
                  <td>{config.material || "-"}</td>
                  <td>{config.tc_no || "-"}</td>
                  <td>{config.vendor_name || "-"}</td>
                  <td>
                    {config.attachment_filename ? (
                      <a
                        href={config.attachment_url || "#"}
                        onClick={(e) => openAttachmentPreview(e, config)}
                        className="attachment-preview-link"
                        title={`Preview: ${config.attachment_filename}`}
                      >
                        {/\.(jpg|jpeg|png)$/i.test(config.attachment_filename) ? "🖼️" : "📄"}
                        {" "}
                        {config.attachment_filename.length > 15
                          ? config.attachment_filename.substring(0, 15) + "..."
                          : config.attachment_filename}
                      </a>
                    ) : (
                      <span className="no-attachment">-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(config)}
                        title="Edit Configuration"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(config.id)}
                        title="Delete Configuration"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  // Render Form View
  const renderFormView = () => (
    <>
      {/* Form Header */}
      <div className="heat-annexure-header">
        <button className="back-button" onClick={handleBackToList}>
          &larr; Back to List
        </button>
        <div className="header-content">
          <h1>{editingId ? "Edit Heat Annexure Configuration" : "Create New Heat Annexure Configuration"}</h1>
          <p>{editingId ? "Update the configuration details below" : "Fill in the details to create a new configuration"}</p>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Form */}
      <form className="heat-annexure-form" onSubmit={handleSubmit}>
        {/* Key Fields Section */}
        <div className="form-section">
          <h3>Configuration Key</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="component">Component *</label>
              <select
                id="component"
                name="component"
                value={formData.component}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Component</option>
                {dropdownOptions.components.map((comp) => (
                  <option key={comp} value={comp}>{comp}</option>
                ))}
              </select>
              <small className="help-text">Components are managed in Standard Master</small>
            </div>
            <div className="form-group">
              <label htmlFor="heat_number">Heat Number *</label>
              <select
                id="heat_number"
                name="heat_number"
                value={formData.heat_number}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Heat Number</option>
                {dropdownOptions.heatNumbers.map((hn) => (
                  <option key={hn} value={hn}>{hn}</option>
                ))}
              </select>
              <small className="help-text">Heat numbers are managed in Standard Master</small>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="form-section">
          <h3>Configuration Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="material">Material</label>
              <input
                type="text"
                id="material"
                name="material"
                value={formData.material}
                onChange={handleInputChange}
                placeholder="Enter or auto-populated from Heat Annexure Configuration"
              />
              <small className="help-text">Auto-populated based on Component, Heat Number, and TC No.</small>
            </div>
            <div className="form-group">
              <label htmlFor="tc_no">TC No.</label>
              <input
                type="text"
                id="tc_no"
                name="tc_no"
                value={formData.tc_no}
                onChange={handleInputChange}
                placeholder="Enter TC number"
              />
            </div>
            <div className="form-group">
              <label htmlFor="vendor_name">Vendor Name</label>
              <input
                type="text"
                id="vendor_name"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleInputChange}
                placeholder="Enter vendor name"
              />
            </div>
          </div>

          {/* Attachment Upload Field */}
          <div className="form-row">
            <div className="form-group attachment-group">
              <label htmlFor="attachment">Attachment (Image/PDF)</label>
              <div className="attachment-upload-area">
                {/* Show preview if we have one and not removing */}
                {attachmentPreview && !removeAttachment ? (
                  <div className="attachment-preview-container">
                    {attachmentPreview.type === "image" ? (
                      <div className="image-preview">
                        <img src={attachmentPreview.url} alt="Attachment preview" />
                        <span className="preview-filename">{attachmentPreview.name}</span>
                      </div>
                    ) : (
                      <div className="pdf-preview">
                        <div className="pdf-icon">📄</div>
                        <span className="preview-filename">{attachmentPreview.name}</span>
                        {existingAttachment && existingAttachment.id && !attachmentFile ? (
                          <a
                            href="#"
                            onClick={(e) => openAttachmentPreview(e, existingAttachment.id)}
                            className="view-pdf-link"
                          >
                            View / Download
                          </a>
                        ) : null}
                      </div>
                    )}
                    <div className="attachment-actions">
                      <button
                        type="button"
                        className="remove-attachment-btn"
                        onClick={handleRemoveAttachment}
                        title="Remove attachment"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="upload-placeholder" onClick={() => !isCompressing && fileInputRef.current?.click()}>
                    {isCompressing ? (
                      <div className="compressing-indicator">
                        <div className="compressing-spinner"></div>
                        <p className="compressing-text">Compressing file, please wait...</p>
                      </div>
                    ) : (
                      <>
                        <div className="upload-icon">📁</div>
                        <p className="upload-text">Click to upload an image or PDF</p>
                        <p className="upload-hint">JPG, PNG, or PDF • Max 1 MB (400 KB after compression)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="attachment"
                      ref={fileInputRef}
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                      disabled={isCompressing}
                    />
                  </div>
                )}
              </div>
              {fileError && <small className="file-error-text">{fileError}</small>}
              <small className="help-text">Accepted: JPG, PNG, PDF (max 400 KB). Images will be compressed if oversized.</small>
            </div>
          </div>
        </div>

        {/* Chemical Analysis Section */}
        <div className="form-section">
          <h3>Chemical Analysis</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="chemical_c">C</label>
              <input
                type="text"
                id="chemical_c"
                name="chemical_c"
                value={formData.chemical_analysis.c}
                onChange={handleInputChange}
                placeholder="Carbon"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_si">Si</label>
              <input
                type="text"
                id="chemical_si"
                name="chemical_si"
                value={formData.chemical_analysis.si}
                onChange={handleInputChange}
                placeholder="Silicon"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_mn">Mn</label>
              <input
                type="text"
                id="chemical_mn"
                name="chemical_mn"
                value={formData.chemical_analysis.mn}
                onChange={handleInputChange}
                placeholder="Manganese"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_p">P</label>
              <input
                type="text"
                id="chemical_p"
                name="chemical_p"
                value={formData.chemical_analysis.p}
                onChange={handleInputChange}
                placeholder="Phosphorus"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="chemical_s">S</label>
              <input
                type="text"
                id="chemical_s"
                name="chemical_s"
                value={formData.chemical_analysis.s}
                onChange={handleInputChange}
                placeholder="Sulfur"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_cr">Cr</label>
              <input
                type="text"
                id="chemical_cr"
                name="chemical_cr"
                value={formData.chemical_analysis.cr}
                onChange={handleInputChange}
                placeholder="Chromium"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_mo">Mo</label>
              <input
                type="text"
                id="chemical_mo"
                name="chemical_mo"
                value={formData.chemical_analysis.mo}
                onChange={handleInputChange}
                placeholder="Molybdenum"
              />
            </div>
            <div className="form-group">
              <label htmlFor="chemical_ni">Ni</label>
              <input
                type="text"
                id="chemical_ni"
                name="chemical_ni"
                value={formData.chemical_analysis.ni}
                onChange={handleInputChange}
                placeholder="Nickel"
              />
            </div>


          </div>
        </div>

        {/* Mechanical Analysis Section */}
        <div className="form-section">
          <h3>Mechanical Analysis</h3>

          {/* Specified Requirement Subsection */}
          <div className="subsection">
            <h4>Specified Requirement</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="mechanical_sr_uts_mpa">UTS Mpa</label>
                <input
                  type="text"
                  id="mechanical_sr_uts_mpa"
                  name="mechanical_sr_uts_mpa"
                  value={formData.mechanical_analysis.specified_requirement.uts_mpa}
                  onChange={handleInputChange}
                  placeholder="UTS MPa"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_sr_ys_1_mpa">Ys(1%) Mpa</label>
                <input
                  type="text"
                  id="mechanical_sr_ys_1_mpa"
                  name="mechanical_sr_ys_1_mpa"
                  value={formData.mechanical_analysis.specified_requirement.ys_1_mpa}
                  onChange={handleInputChange}
                  placeholder="Ys(1%) MPa"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_sr_ys_2_mpa">Ys(2%) Mpa</label>
                <input
                  type="text"
                  id="mechanical_sr_ys_2_mpa"
                  name="mechanical_sr_ys_2_mpa"
                  value={formData.mechanical_analysis.specified_requirement.ys_2_mpa}
                  onChange={handleInputChange}
                  placeholder="Ys(2%) MPa"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="mechanical_sr_el_percent">EL%</label>
                <input
                  type="text"
                  id="mechanical_sr_el_percent"
                  name="mechanical_sr_el_percent"
                  value={formData.mechanical_analysis.specified_requirement.el_percent}
                  onChange={handleInputChange}
                  placeholder="EL %"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_sr_ra_percent">RA%</label>
                <input
                  type="text"
                  id="mechanical_sr_ra_percent"
                  name="mechanical_sr_ra_percent"
                  value={formData.mechanical_analysis.specified_requirement.ra_percent}
                  onChange={handleInputChange}
                  placeholder="RA %"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_sr_hardness_bhn">Hardness BHN</label>
                <input
                  type="text"
                  id="mechanical_sr_hardness_bhn"
                  name="mechanical_sr_hardness_bhn"
                  value={formData.mechanical_analysis.specified_requirement.hardness_bhn}
                  onChange={handleInputChange}
                  placeholder="Hardness BHN"
                />
              </div>
            </div>
          </div>

          {/* Impact Test 1 Subsection */}
          <div className="subsection">
            <h4>Impact Test At-Temp: {formData.mechanical_analysis.impact_test_1.temperature} °C (Min. Joule)</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="mechanical_impact1_min_joule">Min. Joule</label>
                <input
                  type="text"
                  id="mechanical_impact1_min_joule"
                  name="mechanical_impact1_min_joule"
                  value={formData.mechanical_analysis.impact_test_1.min_joule}
                  onChange={handleInputChange}
                  placeholder="Min. Joule"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact1_v1">v1</label>
                <input
                  type="text"
                  id="mechanical_impact1_v1"
                  name="mechanical_impact1_v1"
                  value={formData.mechanical_analysis.impact_test_1.v1}
                  onChange={handleInputChange}
                  placeholder="v1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact1_v2">v2</label>
                <input
                  type="text"
                  id="mechanical_impact1_v2"
                  name="mechanical_impact1_v2"
                  value={formData.mechanical_analysis.impact_test_1.v2}
                  onChange={handleInputChange}
                  placeholder="v2"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact1_v3">v3</label>
                <input
                  type="text"
                  id="mechanical_impact1_v3"
                  name="mechanical_impact1_v3"
                  value={formData.mechanical_analysis.impact_test_1.v3}
                  onChange={handleInputChange}
                  placeholder="v3"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact1_avg">Avg</label>
                <input
                  type="text"
                  id="mechanical_impact1_avg"
                  name="mechanical_impact1_avg"
                  value={formData.mechanical_analysis.impact_test_1.avg}
                  onChange={handleInputChange}
                  placeholder="Average"
                />
              </div>
            </div>
          </div>

          {/* Impact Test 2 Subsection */}
          <div className="subsection">
            <h4>Impact Test At-Temp: {formData.mechanical_analysis.impact_test_2.temperature} °C (Min. Joule)</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="mechanical_impact2_min_joule">Min. Joule</label>
                <input
                  type="text"
                  id="mechanical_impact2_min_joule"
                  name="mechanical_impact2_min_joule"
                  value={formData.mechanical_analysis.impact_test_2.min_joule}
                  onChange={handleInputChange}
                  placeholder="Min. Joule"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact2_v1">v1</label>
                <input
                  type="text"
                  id="mechanical_impact2_v1"
                  name="mechanical_impact2_v1"
                  value={formData.mechanical_analysis.impact_test_2.v1}
                  onChange={handleInputChange}
                  placeholder="v1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact2_v2">v2</label>
                <input
                  type="text"
                  id="mechanical_impact2_v2"
                  name="mechanical_impact2_v2"
                  value={formData.mechanical_analysis.impact_test_2.v2}
                  onChange={handleInputChange}
                  placeholder="v2"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact2_v3">v3</label>
                <input
                  type="text"
                  id="mechanical_impact2_v3"
                  name="mechanical_impact2_v3"
                  value={formData.mechanical_analysis.impact_test_2.v3}
                  onChange={handleInputChange}
                  placeholder="v3"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mechanical_impact2_avg">Avg</label>
                <input
                  type="text"
                  id="mechanical_impact2_avg"
                  name="mechanical_impact2_avg"
                  value={formData.mechanical_analysis.impact_test_2.avg}
                  onChange={handleInputChange}
                  placeholder="Average"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={handleBackToList}>
            Cancel
          </button>
          <button type="submit" className="save-btn" disabled={loading}>
            {loading ? "Saving..." : (editingId ? "Update Configuration" : "Save Configuration")}
          </button>
          {!editingId && (
            <button
              type="button"
              className="save-add-btn"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save & Add Another"}
            </button>
          )}
        </div>
      </form>
    </>
  );

  return (
    <div className="heat-annexure-container">
      {viewMode === "list" ? renderListView() : renderFormView()}
    </div>
  );
}

export default HeatAnnexure;
